import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AIRTABLE_URL, AIRTABLE_HEADERS } from "./constants";


export default function MyEarnings() {
  const [orders, setOrders] = useState([]);
  const [totalEarnings, setTotalEarnings] = useState({ total: 0, paid: 0, balance: 0 });
  const [monthlyEarnings, setMonthlyEarnings] = useState({});
  const [selectedYear, setSelectedYear] = useState(null);
  const technicianPhone = localStorage.getItem("technicianPhone")?.trim();
  const navigate = useNavigate();

  const TECHNICIAN_SHARE = 0.85;

  // ✅ Memoized month names
  const monthNames = useMemo(
    () => [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ],
    []
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
       const res = await fetch(AIRTABLE_URL, {
  headers: AIRTABLE_HEADERS
});
const json = await res.json();

        const technicianPhone = localStorage.getItem("technicianPhone");

        const records = json.records.map(rec => ({
          id: rec.id,
          ...rec.fields
        }));

        const filtered = records.filter(
          (item) =>
            item?.assignedTechnicianPhone?.trim() === technicianPhone &&
            item?.paid?.toString().toLowerCase() === "true"
        );

        let total = 0;
        let paidTotal = 0;
        const yearlyMap = {};

        const processed = filtered.map((item) => {
          const service = parsePrice(item.total || item.services);
          const extra = parseExtraPartsTotal(item.extraParts);
          const grand = service + extra;
          const earning = Math.round(grand * TECHNICIAN_SHARE);
          const paid = parseInt(item.technicianEarning || 0);

          total += earning;
          paidTotal += paid;

          const [datePart] = (item.slot || "").split(" at ");
          let year, monthIndex, day;

          if (datePart.includes("-")) {
            const parts = datePart.split("-").map(n => parseInt(n, 10));
            year = parts[0];
            monthIndex = parts[1] - 1;
            day = parts[2];
          } else if (datePart.includes("/")) {
            const parts = datePart.split("/").map(n => parseInt(n, 10));
            year = parts[2];
            monthIndex = parts[1] - 1;
            day = parts[0];
          } else {
            console.warn("Unknown date format:", datePart);
            return null;
          }

          const monthYear = `${monthNames[monthIndex]}-${year}`;
          if (!yearlyMap[year]) yearlyMap[year] = {};
          yearlyMap[year][monthYear] = (yearlyMap[year][monthYear] || 0) + earning;

          return {
            ...item,
            service,
            extra,
            grand,
            earning,
            paid,
            year,
            monthIndex,
            dateObj: new Date(year, monthIndex, day || 1)
          };
        }).filter(Boolean);

        processed.sort((a, b) => b.dateObj - a.dateObj);

        setOrders(processed);
        setTotalEarnings({ total, paid: paidTotal, balance: total - paidTotal });
        setMonthlyEarnings(yearlyMap);

        const allYears = Object.keys(yearlyMap).map(Number).sort((a, b) => b - a);
        setSelectedYear(allYears[0]);
      } catch (error) {
        console.error("Error fetching earnings:", error);
      }
    };

    if (technicianPhone) fetchData();
  }, [technicianPhone, monthNames]);

  const parsePrice = (str) => {
  if (!str) return 0;

  // Match like: ₹500, ₹500x2, ₹500 x 2
  const matches = [...str.matchAll(/₹\s?(\d+)(?:\s?[xX]\s?(\d+))?/g)];

  return matches.reduce((sum, match) => {
    const price = parseInt(match[1], 10);
    const qty = match[2] ? parseInt(match[2], 10) : 1; // default 1 if no quantity
    return sum + price * qty;
  }, 0);
};


  const parseExtraPartsTotal = (extraStr) => {
    if (!extraStr || extraStr === "None") return 0;
    return extraStr.split(",").reduce((sum, part) => {
      const match = part.match(/₹\s?(\d+)/);
      return sum + (match ? parseInt(match[1]) : 0);
    }, 0);
  };

  return (
    <div style={{ padding: "40px 0", position: "relative" }}>
      <button
        onClick={() => navigate(-1)}
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          background: "none",
          border: "none",
          fontSize: 20,
          cursor: "pointer",
          color: "Black"
        }}
      >
        ✖
      </button>

      <h2>💼 My Earnings</h2>

      <div style={styles.totalSummary}>
        <h3>💰 Total Earnings (85%) : ₹{totalEarnings.total}</h3>
        <p>✅ Paid: ₹{totalEarnings.paid}</p>
        <p>🕗 Balance: ₹{totalEarnings.balance}</p>
      </div>

      <div style={{ marginBottom: 20 }}>
        <label><strong>Select Year: </strong></label>
        <select value={selectedYear || ""} onChange={(e) => setSelectedYear(Number(e.target.value))}>
          {Object.keys(monthlyEarnings)
            .map(Number)
            .sort((a, b) => b - a)
            .map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
        </select>
      </div>

      {/* 📅 Monthly Earnings */}
      <div style={styles.monthBox}>
        <h4>📅 Monthly Earnings ({selectedYear}):</h4>
        {selectedYear && monthlyEarnings[selectedYear] &&
          Object.entries(monthlyEarnings[selectedYear])
            .sort((a, b) => monthNames.indexOf(a[0].split("-")[0]) - monthNames.indexOf(b[0].split("-")[0]))
            .map(([monthYear, amount]) => (
              <div key={monthYear} style={styles.monthRow}>
                {monthYear} → ₹{amount}
              </div>
            ))}
      </div>

      {/* Orders filtered by year */}
      {(() => {
        const filteredOrders = selectedYear
          ? orders.filter((order) => order.year === selectedYear)
          : orders;

        return filteredOrders.length === 0 ? (
          <p>No paid orders found for {selectedYear}.</p>
        ) : (
          filteredOrders.map((item, index) => (
           <div key={index} style={styles.card}>
  <div style={styles.row}><strong>Order ID:</strong> {item.orderId}</div>
  <div style={styles.row}><strong>Service:</strong> {item.services}</div>
  <div style={styles.row}><strong>Service Total:</strong> ₹{item.service}</div>

  {/* ✅ Extra Parts aur Extra Parts Total alag show hoga */}
  <div style={styles.row}><strong>Extra Parts:</strong> {item.extraParts || "None"}</div>
  <div style={styles.row}><strong>Extra Parts Total:</strong> ₹{item.extra}</div>

  <div style={styles.row}><strong>Grand Total:</strong> ₹{item.grand}</div>
  <div style={styles.row}><strong>Your Earning (85%):</strong> ₹{item.earning}</div>
  <div style={styles.row}><strong>Paid:</strong> ₹{item.paid}</div>
  <div style={styles.row}><strong>Balance:</strong> ₹{item.earning - item.paid}</div>
              <div style={styles.row}>
                <strong>Status:</strong>{" "}
                {item.paid >= item.earning ? (
                  <span style={{ color: "green", fontWeight: "bold" }}>✅ Paid in Full</span>
                ) : (
                  <span style={{ color: "orange", fontWeight: "bold" }}>
                    ⏳ Partial Payment: ₹{item.earning - item.paid} remaining
                  </span>
                )}
              </div>
              <div style={styles.row}><strong>Date:</strong> {item.slot?.split(",")[0] || "N/A"}</div>
            </div>
          ))
        );
      })()}
    </div>
  );
}

const styles = {
  totalSummary: {
    marginBottom: 20,
    padding: 15,
    backgroundColor: "#d4edda",
    borderRadius: 6,
    border: "1px solid #c3e6cb",
  },
  monthBox: {
    marginBottom: 20,
    padding: 15,
    backgroundColor: "#fdf8e4",
    borderRadius: 6,
    border: "1px solid #f5c542",
  },
  monthRow: { padding: "4px 0" },
  card: {
    border: "1px solid #ccc",
    borderRadius: 8,
    padding: 15,
    marginBottom: 15,
    background: "#f4f4f4",
  },
  row: { marginBottom: 4 },
};
